import { useCallback } from "react";
import { trackError } from "../../utils/error-tracking";
import { getRecaptchaToken, RECAPTCHA_READY_TIMEOUT_MS } from "../../utils/recaptcha-token";
import { recaptchaClient } from "../../utils/recaptcha-client";
import { asError } from '../../hooks/as-error';

// grecaptcha types declared in global.d.ts

export { RECAPTCHA_READY_TIMEOUT_MS };

/**
 * useRecaptcha Hook
 *
 * Provides reCAPTCHA v3 functionality for bot protection
 *
 * @returns Object containing recaptcha execution functions
 */
export const useRecaptcha = () => {
	/**
	 * Execute reCAPTCHA v3 and get token
	 * @param action - The action name for this recaptcha execution
	 * @returns Promise resolving to recaptcha token or undefined
	 */
	const executeRecaptcha = useCallback((action: string = 'login'): Promise<string | undefined> => getRecaptchaToken(action), []);

	/**
	 * Verify reCAPTCHA token and get score
	 * @param token - The recaptcha token to verify
	 * @returns Promise resolving to score (0-1) or 0 on error
	 */
	const verifyToken = useCallback(async (token: string): Promise<number> => {
		try {
			const response = await fetch('/api/recaptcha-verify', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ token }),
			});

			const data: { success?: boolean; score?: number } = await response.json();

			if (data?.success) {
				return data.score || 0;
			}
			return 0;
		} catch (e) {
			trackError(asError(e), undefined, { action: 'verifyToken', component: 'useRecaptcha' });
			return 0;
		}
	}, []);

	/**
	 * Initialize reCAPTCHA when ready
	 */
	const initRecaptcha = useCallback(() => {
		try {
			recaptchaClient()?.ready?.(() => {
				// reCAPTCHA is ready
			});
		} catch (e) {
			trackError(asError(e), undefined, { action: 'initRecaptcha', component: 'useRecaptcha' });
		}
	}, []);

	return {
		executeRecaptcha,
		verifyToken,
		initRecaptcha,
	};
};
